/**
 * Org-level statutory number formats (India) — clear, user-facing rules.
 * Used by StatutoryComponents guided setup for validation + input hints.
 */

export const STATUTORY_FORMATS = {
  epfo_establishment_code: {
    label: "EPFO Establishment Code",
    hint: "Format: MH/BAN/1234567/000  (Region/Office/Establishment/Extension)",
    example: "MH/BAN/1234567/000",
    // Region(2) / Office(3) / Est(1–7) / Ext(optional, 1–7) — slashes or hyphens allowed.
    // State-specific rules for the EPF step live in PF_STATE_CONFIG below.
    pattern: /^[A-Z]{2}[/-][A-Z]{3}[/-]\d{1,7}(?:[/-][A-Z0-9]{1,7})?$/i,
    // The EPF input auto-inserts a trailing "/" after a full segment — drop it.
    normalize: (v) => String(v || "").trim().toUpperCase().replace(/\s+/g, "").replace(/\/+$/, ""),
  },
  esic_registration_number: {
    label: "ESIC Registration Number",
    hint: "Format: 17 digits only",
    example: "31001234560000999",
    pattern: /^\d{17}$/,
    // Structural check only — it does not verify the number with ESIC.
    // Internal spaces are invalid, so only trim the ends.
    normalize: (v) => String(v || "").trim(),
    requiredError: "ESI Employer / Establishment Registration No. is required.",
    invalidError: "Please enter exactly 17 digits. Format: 17 digits only.",
  },
  pt_registration_number: {
    label: "PT Registration Number",
    hint: "Format: 5–20 letters/numbers  (as printed on your state PT certificate)",
    example: "PT-KA-123456",
    pattern: /^[A-Z0-9][A-Z0-9/-]{4,19}$/i,
    normalize: (v) => String(v || "").trim().toUpperCase().replace(/\s+/g, ""),
  },
  pt_enrollment_number: {
    label: "PT Enrollment Number",
    hint: "Format: 5–20 letters/numbers  (state enrollment / TIN style ID)",
    example: "ENR1234567",
    pattern: /^[A-Z0-9][A-Z0-9/-]{4,19}$/i,
    normalize: (v) => String(v || "").trim().toUpperCase().replace(/\s+/g, ""),
  },
  company_pan: {
    label: "Company PAN",
    hint: "Format: AAAAA9999A  (5 letters + 4 digits + 1 letter)",
    example: "ABCDE1234F",
    pattern: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
    normalize: (v) => String(v || "").trim().toUpperCase().replace(/\s+/g, ""),
  },
  company_tan: {
    label: "Company TAN",
    hint: "Format: AAAA99999A  (4 letters + 5 digits + 1 letter)",
    example: "ABCD12345E",
    pattern: /^[A-Z]{4}[0-9]{5}[A-Z]$/,
    normalize: (v) => String(v || "").trim().toUpperCase().replace(/\s+/g, ""),
  },
  gst_number: {
    label: "GST Number",
    hint: "Format: 15-character GSTIN  (e.g. 27ABCDE1234F1Z5)",
    example: "27ABCDE1234F1Z5",
    pattern: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/,
    normalize: (v) => String(v || "").trim().toUpperCase().replace(/\s+/g, ""),
  },
  cin_number: {
    label: "CIN Number",
    hint: "Format: 21-character CIN  (e.g. U12345MH2020PTC123456)",
    example: "U12345MH2020PTC123456",
    pattern: /^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/,
    normalize: (v) => String(v || "").trim().toUpperCase().replace(/\s+/g, ""),
  },
};

/** Validate a single field. Empty is OK unless `required` is true. */
export function validateStatutoryFormat(key, raw, { required = false } = {}) {
  const spec = STATUTORY_FORMATS[key];
  if (!spec) return { ok: true, value: raw, error: null };

  const empty = raw == null || String(raw).trim() === "";
  if (empty) {
    if (required) {
      return {
        ok: false,
        value: "",
        error: spec.requiredError || `${spec.label} is required. Enter it like: ${spec.example}`,
      };
    }
    return { ok: true, value: "", error: null };
  }

  const value = spec.normalize(raw);
  if (!spec.pattern.test(value)) {
    return {
      ok: false,
      value,
      error: spec.invalidError || `Invalid ${spec.label}. Must match: ${spec.hint}`,
    };
  }
  return { ok: true, value, error: null };
}

export function formatHint(key) {
  return STATUTORY_FORMATS[key]?.hint || "";
}

export function formatExample(key) {
  return STATUTORY_FORMATS[key]?.example || "";
}

/**
 * EPF registration number rules per state. Add a state here to support it —
 * the dropdown, input mask and validation all read from this map.
 */
// Spelled out so users know which parts are letters and which are numbers.
const PF_PARTS = "office code: 3 letters, establishment no.: 1–7 digits, extension: optional, up to 3 letters/numbers";

export const PF_STATE_CONFIG = {
  Telangana: {
    prefix: "TS",
    placeholder: "TS/HYD/1234567/000",
    format: "Format: TS/Office/Establishment/Extension",
    regex: /^TS\/[A-Z]{3}\/\d{1,7}(?:\/[A-Z0-9]{1,3})?$/,
    error: `Invalid PF Registration Number. Please follow the required format: TS/HYD/1234567/000 (${PF_PARTS}).`,
  },
  "Andhra Pradesh": {
    prefix: "AP",
    placeholder: "AP/XXX/1234567/000",
    format: "Format: AP/Office/Establishment/Extension",
    regex: /^AP\/[A-Z]{3}\/\d{1,7}(?:\/[A-Z0-9]{1,3})?$/,
    error: `Invalid PF Registration Number. Please follow the required format: AP/ABC/1234567/000 (${PF_PARTS}).`,
  },
};

export const DEFAULT_PF_STATE = "Telangana";
export const PF_INVALID_CHAR_ERROR = "Only uppercase letters and numbers are allowed in this section.";

// Why a typed / pasted character did not fit, by the section it landed in.
const PF_SECTION_ERRORS = [
  "Invalid PF Registration Number. The office code must be 3 letters (e.g. HYD) — numbers are not allowed here.",
  "Invalid PF Registration Number. The establishment number must be digits only (1–7 digits, e.g. 1234567).",
  "Invalid PF Registration Number. The extension can have at most 3 letters/numbers (e.g. 000).",
];

/** Most helpful message for a formatter result that refused characters. */
export function pfRefusalError(res, state) {
  if (res?.invalid > 0) return PF_INVALID_CHAR_ERROR;
  if (res?.dropSection != null) return PF_SECTION_ERRORS[res.dropSection];
  return getPfStateConfig(state).error;
}
export const PF_STATE_OPTIONS = Object.keys(PF_STATE_CONFIG);

export function getPfStateConfig(state) {
  return PF_STATE_CONFIG[state] || PF_STATE_CONFIG[DEFAULT_PF_STATE];
}

/** State whose prefix the code carries (e.g. "TS/..." → Telangana), or null. */
export function detectPfState(code) {
  const prefix = String(code || "").trim().toUpperCase().slice(0, 3);
  return PF_STATE_OPTIONS.find((s) => `${PF_STATE_CONFIG[s].prefix}/` === prefix) || null;
}

// Segments after the state prefix: Office / Establishment / Extension.
const PF_SEGMENTS = [
  { accepts: (c) => /[A-Z]/.test(c), max: 3 },
  { accepts: (c) => /\d/.test(c), max: 7 },
  { accepts: (c) => /[A-Z0-9]/.test(c), max: 3 },
];

/**
 * Mask the part after "XX/": uppercases, drops invalid characters, inserts "/"
 * between segments and caps each segment's length. `dropped` counts letters /
 * digits that did not fit and `invalid` counts other characters (spaces,
 * symbols), so callers can refuse lossy edits and pastes.
 */
function formatPfBody(raw, { trailingSlash = true } = {}) {
  const segs = ["", "", ""];
  let i = 0;
  let dropped = 0;
  let invalid = 0;
  let dropSection = null;
  for (const c of String(raw || "").toUpperCase()) {
    const seg = PF_SEGMENTS[i];
    if (c === "/") {
      // Explicit separator ends a non-empty segment early; never yields "//".
      if (segs[i] && i < 2) i += 1;
      continue;
    }
    if (!/[A-Z0-9]/.test(c)) {
      invalid += 1;
      continue;
    }
    if (seg.accepts(c) && segs[i].length < seg.max) {
      segs[i] += c;
    } else if (i < 2 && segs[i].length === seg.max && PF_SEGMENTS[i + 1].accepts(c)) {
      // Continuous typing: a full segment rolls over into the next one.
      i += 1;
      segs[i] += c;
    } else {
      dropped += 1;
      if (dropSection == null) dropSection = i;
    }
  }
  let body = segs.slice(0, i + 1).join("/");
  if (trailingSlash && i < 2 && segs[i].length === PF_SEGMENTS[i].max) body += "/";
  return { body, dropped, invalid, dropSection, segs, section: i };
}

/**
 * Format a full value that starts with the state's "XX/" prefix.
 * Returns null when the prefix is missing or damaged.
 */
export function formatPfRegistration(raw, state, opts) {
  const lead = `${getPfStateConfig(state).prefix}/`;
  const s = String(raw || "").toUpperCase();
  if (!s.startsWith(lead)) return null;
  const { body, ...rest } = formatPfBody(s.slice(lead.length), opts);
  return { value: lead + body, ...rest };
}

/** Validate the EPF registration number against the selected state's format. */
export function validatePfRegistration(raw, state, { required = false } = {}) {
  const cfg = getPfStateConfig(state);
  // A trailing "/" is the mask's auto-separator, not part of the number.
  const value = String(raw || "").trim().toUpperCase().replace(/\/+$/, "");
  if (!value || value === cfg.prefix) {
    if (required) {
      return {
        ok: false,
        value: "",
        error: `PF Establishment / Registration No. is required. Enter it like: ${cfg.placeholder}`,
      };
    }
    return { ok: true, value: "", error: null };
  }
  if (!cfg.regex.test(value)) return { ok: false, value, error: cfg.error };
  return { ok: true, value, error: null };
}

/**
 * Live status while typing. `error` is set only for values that cannot become
 * valid by typing more; partial-but-possible input gets a soft `hint` instead.
 */
export function pfLiveStatus(raw, state) {
  const cfg = getPfStateConfig(state);
  const lead = `${cfg.prefix}/`;
  const value = String(raw || "").trim().toUpperCase();
  if (!value || value === lead) return { error: null, hint: "Enter the 3-letter office code (e.g. HYD)." };
  const res = formatPfRegistration(value, state);
  const strip = (v) => v.replace(/\/+$/, "");
  if (!res || res.dropped || res.invalid || strip(res.value) !== strip(value)) {
    return { error: /[^A-Z0-9/]/.test(value) ? PF_INVALID_CHAR_ERROR : res ? pfRefusalError(res, state) : cfg.error, hint: null };
  }
  if (cfg.regex.test(strip(value))) {
    return {
      error: null,
      hint: res.segs[2] ? "Format complete." : "Format complete. Extension (1–3 letters/numbers) is optional.",
    };
  }
  if (res.section === 0 && res.segs[0].length < 3) {
    return { error: null, hint: "Office code needs 3 letters." };
  }
  return { error: null, hint: "Next: establishment number (1–7 digits)." };
}

/** Guided setup step order (one-after-one). */
export const STATUTORY_SETUP_STEPS = [
  { id: "registrations", label: "Government Registrations", short: "Registrations" },
  { id: "epf", label: "EPF", short: "EPF" },
  { id: "esi", label: "ESI", short: "ESI" },
  { id: "pt", label: "Professional Tax", short: "PT" },
  { id: "lwf", label: "Labour Welfare Fund", short: "LWF" },
  { id: "bonus", label: "Statutory Bonus", short: "Bonus" },
  { id: "dashboard", label: "Completion", short: "Done" },
];

export const STATUTORY_STEP_KEY = "brixigo.statutorySetup.step";
